import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { ArchImage } from "@/components/ArchImage";
import { SITE, IMAGES } from "@/data/content";

const RELEASE = [
  "All information shared by the client during a session remains strictly confidential as required by law. Craniosacral techniques are provided with the client fully clothed; some myofascial release techniques may require shorts so that skin contact can be made.",
  "Laura McLelland agrees to be fully present during sessions, to answer questions to the best of her knowledge, and to offer a safe, supportive environment to facilitate a healing process.",
  "I understand that bodywork does not replace medical treatment. Healing is a process, not an event, and I agree to take responsibility for my own healing by communicating clearly with my therapist about conditions of body or mind that may affect the process.",
  "I understand craniosacral therapy is not a substitute for medical examination, diagnosis, or treatment, and that craniosacral therapists do not diagnose, prescribe, or treat illness. I affirm I have stated all known medical conditions honestly and will keep the therapist updated on any changes.",
];

const field =
  "w-full bg-white/70 border border-cove-slate/20 rounded-xl px-4 py-3 text-cove-slate placeholder:text-cove-taupe/70 focus:outline-none focus:ring-2 focus:ring-cove-slate/40";
const labelCls = "block text-sm font-medium text-cove-olive mb-1.5";

const Text = ({ label, name, type = "text", required, placeholder }) => (
  <div>
    <label htmlFor={name} className={labelCls}>{label}{required && <span className="text-cove-rose"> *</span>}</label>
    <input id={name} name={name} type={type} required={required} placeholder={placeholder} className={field} data-testid={`intake-${name}`} />
  </div>
);

const Area = ({ label, name, rows = 3 }) => (
  <div>
    <label htmlFor={name} className={labelCls}>{label}</label>
    <textarea id={name} name={name} rows={rows} className={field} data-testid={`intake-${name}`} />
  </div>
);

export default function Intake() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const allInitialed = RELEASE.every((_, i) => form[`initial_${i}`]?.checked);
    if (!allInitialed) { toast.error("Please initial each statement in the Release of Liability."); return; }

    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form));
    try {
      await fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: data.toString() });
    } catch (err) { /* not on Netlify in preview, succeed gracefully */ }
    toast.success("Thank you, your intake form has been submitted securely.");
    setDone(true);
    setSubmitting(false);
    form.reset();
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Seo
        title="Client Intake Form"
        description="Confidential craniosacral therapy client intake form for Cranio Cove, Irvington NY. Please complete before your first session with Laura McLelland."
        path="/intake"
      />
      <PageHero
        overline="Before your visit"
        titleLines={["Client", "Intake Form"]}
        intro="Please complete this confidential form before your first session. Your information helps Laura prepare a session that's safe and tailored to you."
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "Intake Form", path: "/intake" }])}
        heroImage={{ src: IMAGES.coveBrightSky, alt: "A bright, open sky with soft clouds reflected in a calm lake" }}
      />

      <section className="px-6 md:px-12 pb-28 md:pb-36" data-testid="intake-section">
        <div className="mx-auto max-w-3xl">
          {done ? (
            <Reveal>
              <div className="rounded-[2rem] bg-cove-sage/20 border border-cove-slate/10 p-10 text-center">
                <h2 className="font-serif text-3xl text-cove-slate">Received with thanks.</h2>
                <p className="mt-4 text-cove-olive">Your intake form has been submitted. Laura will review it before your session. If anything changes, just let her know at {SITE.phone}.</p>
              </div>
            </Reveal>
          ) : (
          <Reveal>
            <form
              name="intake"
              method="POST"
              data-netlify="true"
              data-testid="intake-form"
              onSubmit={handleSubmit}
              className="space-y-10"
              noValidate
            >
              <input type="hidden" name="form-name" value="intake" />
              <p className="hidden"><label>Skip if human: <input name="bot-field" /></label></p>

              <fieldset className="space-y-5">
                <legend className="font-serif text-2xl text-cove-slate mb-2">Your details</legend>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Text label="Full name" name="name" required />
                  <Text label="Age" name="age" />
                  <Text label="Occupation" name="occupation" />
                  <Text label="Phone" name="phone" type="tel" required />
                  <Text label="Email" name="email" type="email" required />
                  <Text label="Referred by" name="referred_by" />
                </div>
                <Text label="Address" name="address" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Text label="Emergency contact" name="emergency_contact" />
                  <Text label="Emergency contact phone" name="emergency_phone" type="tel" />
                </div>
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="font-serif text-2xl text-cove-slate mb-2">Medical history</legend>
                <Area label="Please describe any medical conditions" name="conditions" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Area label="How & when did this begin?" name="condition_onset" rows={2} />
                  <Area label="What aggravates / relieves it?" name="condition_triggers" rows={2} />
                  <Text label="Treating physician & phone" name="physician" />
                  <Text label="OK to call physician? (yes/no)" name="ok_to_call" />
                  <Text label="Blood pressure in normal range?" name="blood_pressure" />
                  <Text label="Currently pregnant? (if applicable)" name="pregnant" />
                </div>
                <Area label="Medications you are currently taking (and any side effects)" name="medications" rows={2} />
                <Area label="Any stiffness/restricted motion, arthritis, bursitis, or varicose veins?" name="mobility" rows={2} />
                <Area label="Surgeries & dates" name="surgeries" rows={2} />
                <Area label="Injuries (broken bones, whiplash, severe sprains, etc.)" name="injuries" rows={2} />
                <Area label="Chronic conditions (e.g. diabetes, epilepsy)" name="chronic" rows={2} />
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="font-serif text-2xl text-cove-slate mb-2">Lifestyle</legend>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Text label="Eye impairments / corrective lenses" name="vision" />
                  <Text label="Type & frequency of exercise" name="exercise" />
                  <Text label="Relaxation or meditation habits" name="relaxation" />
                  <Text label="Dietary habits" name="diet" />
                  <Text label="Drug or alcohol use" name="substances" />
                </div>
                <Area label="Other types of therapy (kind & how often)?" name="other_therapy" rows={2} />
                <Area label="Previous experience with craniosacral therapy, if any" name="cst_experience" rows={2} />
                <Area label="Goals for treatment" name="goals" rows={3} />
              </fieldset>

              <fieldset className="space-y-4">
                <legend className="font-serif text-2xl text-cove-slate mb-2">Release of liability</legend>
                <p className="text-sm text-cove-olive">Please initial each statement to acknowledge you understand and agree.</p>
                {RELEASE.map((r, i) => (
                  <label key={i} htmlFor={`initial_${i}`} className="flex items-start gap-4 rounded-xl bg-white/50 border border-cove-slate/15 p-4 cursor-pointer">
                    <input id={`initial_${i}`} name={`initial_${i}`} type="checkbox" value="initialed" className="mt-1 h-5 w-5 accent-[#293650] shrink-0" data-testid={`intake-initial-${i}`} />
                    <span className="text-sm leading-relaxed text-cove-slate">{r}</span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="font-serif text-2xl text-cove-slate mb-2">Signature</legend>
                <p className="text-sm text-cove-olive">By typing your name below, you waive and release Laura McLelland (craniosacral practitioner) from any liability, past, present, and future, relating to craniosacral session work.</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Text label="Client name (signature)" name="signature" required />
                  <Text label="Date" name="signature_date" type="date" required />
                </div>
              </fieldset>

              <button type="submit" disabled={submitting} data-testid="intake-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-cove-slate px-8 py-4 text-sm font-medium tracking-wide text-cove-cream transition-colors duration-300 hover:bg-cove-olive disabled:opacity-60">
                {submitting ? "Submitting..." : "Submit Intake Form"}
              </button>

              <p className="flex items-start gap-2 text-xs text-cove-taupe leading-relaxed">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-cove-sage" />
                This form is confidential and used only to support your care. Submissions are handled securely via Netlify Forms.
              </p>
            </form>
          </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
